import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { FormControl, ValidatorFn, Validators, FormGroup, FormBuilder } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialog } from '@angular/material/dialog';
import { DataBagService } from '@/common/data-bag.service';
import { InfoboxService } from '@/common/infobox/infobox.service';
import { DateService } from '@/common/date.service';
import { User, Departments, Kind } from '../task.model';
import { TasksService } from '../tasks.service';
import { ConfirmTaskDialogComponent } from '../tasks-list/confirm-task-dialog/confirm-task-dialog.component';
import { LoaderService } from '@/common/loader/loader.service';
import { debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { TableName } from '@/common/enums/TableName';
import { DatePipe } from '@angular/common';

@Component({
	selector: 'app-tasks-dialog',
	templateUrl: './tasks-dialog.component.html',
	styleUrls: ['./tasks-dialog.component.scss'],
	providers: [DatePipe],
})
export class TasksDialogComponent implements OnInit {
	constructor(
		@Inject(MAT_DIALOG_DATA) public data: any,
		public dialogRef: MatDialogRef<any>,
		public fb: FormBuilder,
		public infobox: InfoboxService,
		public bag: DataBagService,
		public date: DateService,
		public service: TasksService,
		protected dialog: MatDialog,
		protected loader: LoaderService,
		private readonly datePipe: DatePipe,
	) {
		this.reactiveForm();
	}

	minDate: Date = new Date();
	currentTime: string = this.date.now().time;
	taskForm: FormGroup;
	users: User[] = [];
	filteredUsers: User[] = [];
	departments: Departments[] = [];
	status = [];
	kinds: Kind[] = [];
	$status: Subject<any> = new Subject<any>();
	@ViewChild('pickerD') timePicker: any;
	protected readonly TableName = TableName;

	ngOnInit() {
		this.getUsers();
		this.getDepartments();
		this.getStatus();
		this.getKinds();
		console.log(this.data);
		if (this.data.task !== undefined) {
			this.currentTime = this.datePipe.transform(this.data.task.deadline, 'HH:mm');
		}
		if (!this.data.editable) {
			Object.keys(this.taskForm.controls).some((controlName) => this.taskForm.controls[controlName].disable());
		}

		this.taskForm
			.get('usersSearch')
			.valueChanges.pipe(debounceTime(300), distinctUntilChanged())
			.subscribe((searchValue) => {
				if (!searchValue.length) {
					return (this.filteredUsers = this.users);
				}
				this.bag.get(`/profile/all_users/?full_name=${searchValue}`, (result: { results: User[] }, error) => {
					this.filteredUsers = error
						? []
						: result.results.filter((v) => v.firstName !== '' && v.lastName !== '');
				});
			});
	}

	private getUsers() {
		this.loader.show();
		this.bag.get('/profile/all_users/?limit=9999', (result, error) => {
			if (result) {
				this.users = result.results.filter((item) => item.firstName !== '' && item.lastName !== '');
				this.filteredUsers = this.users;
				if (this.data.task !== undefined) {
					this.selectFormValue('responsibleUser', this.data.task.users);
					this.loader.hide();
					return;
				}
				this.service.loadProfile().then((user) => {
					this.taskForm.get('responsibleUser').setValue([this.users.find((item) => item.id === user.id).id]);
				});
			}
			this.loader.hide();
		});
	}

	private getDepartments() {
		this.loader.show();
		this.bag.get('/organization/department_kind/?limit=9999', (result, error) => {
			this.bag.get('/organization/department/?limit=50&organization=5', (departments, error) => {
				if (result)
					this.departments = departments.results.map((department) => {
						return {
							id: department.id,
							name: result.results.find((kind) => kind.id === department.kind).name,
						};
					});
				if (this.data.task !== undefined)
					this.selectFormValue(
						'responsibleOrganization',
						this.data.task.responsibilityDepartments.map((department) => {
							return {
								id: department.id,
								name: department.kind.name,
							};
						}),
					);
			});
			this.loader.hide();
		});
	}

	private getStatus() {
		this.loader.show();
		this.bag.get('/task_note/status/', (result, error) => {
			if (result) {
				this.status = result.results;
				this.$status.next(result.results);
			}
			this.loader.hide();
		});
	}

	private getKinds() {
		this.loader.show();
		this.bag.get('/task_note/kind/?uses__id__in=RES,TASK', (result, error) => {
			if (result) {
				this.kinds = result.results;
				if (this.data.task !== undefined) this.selectFormValue('kind', this.data.task.kinds);
			}
			this.loader.hide();
		});
	}

	private selectFormValue(form: string, data: any) {
		this.taskForm.get(form).setValue(data.map((item) => item.id));
	}

	private getDeadlineDate() {
		if (this.data.task !== undefined) return new Date(this.data.task.deadline);
		return new Date();
	}

	openPicker() {
		if (!this.data.editable) return;
		this.timePicker.open();
	}

	reactiveForm() {
		this.taskForm = this.fb.group({
			description: new FormControl(this.data.task ? this.data.task.content : (this.data.content ?? null), [
				Validators.required,
			]),
			deadlineTime: new FormControl('', [Validators.required]),
			deadlineDate: new FormControl(this.getDeadlineDate(), [Validators.required]),
			status: new FormControl('', [Validators.required]),
			kind: new FormControl('', [Validators.required]),
			responsibleUser: new FormControl(''),
			responsibleOrganization: new FormControl(''),
			usersSearch: new FormControl(''),
		});
		this.$status.subscribe((val) => {
			this.taskForm
				.get('status')
				.setValue(this.data.task ? this.data.task?.status.id : val.find((v) => v.name === 'W trakcie').id);
		});
		const emailValidator: ValidatorFn = (group: FormGroup) => {
			const responsibleUser = group.get('responsibleUser');
			const responsibleOrganization = group.get('responsibleOrganization');

			if (responsibleUser.value.length === 0 && responsibleOrganization.value.length === 0) {
				if (responsibleUser.validator === null && responsibleOrganization.validator === null) {
					responsibleUser.setValidators([Validators.required]);
					responsibleUser.updateValueAndValidity();
					responsibleOrganization.setValidators([Validators.required]);
					responsibleOrganization.updateValueAndValidity();
					responsibleUser.markAllAsTouched();
					responsibleOrganization.markAsTouched();
				}
				return { userError: true };
			} else {
				if (responsibleUser.validator !== null && responsibleOrganization.validator !== null) {
					responsibleUser.clearValidators();
					responsibleUser.updateValueAndValidity();
					responsibleOrganization.clearValidators();
					responsibleOrganization.updateValueAndValidity();
				}

				return { userError: false };
			}
		};
		this.taskForm.setValidators(emailValidator);
	}

	private setDeadline() {
		const dateObject = new Date(this.taskForm.value.deadlineDate);
		const [hours, minutes] = this.taskForm.value.deadlineTime.split(':').map(Number);
		dateObject.setHours(hours);
		dateObject.setMinutes(minutes);
		return dateObject;
	}

	private setDateToSend(completedBy, completed, responsibleOrganization, responsibleUser) {
		let tableName: string;
		let tableId: number | string;
		if (this.data.task) {
			tableId = this.data.task.tableId;
			tableName = this.data.task.tableName;
		} else {
			tableId = this.data.tableName === TableName.USER_PROFILE ? this.service.loggedUser.id : this.data.tableId;
			tableName = this.data.tableName;
		}
		return {
			deadline: this.setDeadline(),
			completed_by: completedBy,
			completed: completed,
			responsibilityDepartments: responsibleOrganization,
			users: responsibleUser,
			content: this.taskForm.value.description,
			multimedia: [],
			table_id: tableId,
			table_name: tableName,
			kinds: this.taskForm.value.kind,
			status: this.taskForm.value.status,
		};
	}

	submit() {
		const hasInvalidControl = Object.keys(this.taskForm.controls).some(
			(controlName) => this.taskForm.controls[controlName].invalid,
		);
		if (hasInvalidControl) {
			this.infobox.error('Uzupełnij poprawnie wszystkie pola');
			return;
		}

		const responsibleUser = this.taskForm.value.responsibleUser
			? this.taskForm.value.responsibleUser.map((user) => {
					return user;
				})
			: [];

		const responsibleOrganization = this.taskForm.value?.responsibleOrganization
			? this.taskForm.value?.responsibleOrganization.map((organization) => {
					return organization;
				})
			: [];

		let completed = null;
		let completedBy = null;
		if (this.taskForm.value.status === 1) {
			completed = new Date().toISOString();
			completedBy = this.service.loggedUser.id;
			this.dialog
				.open(ConfirmTaskDialogComponent, {
					width: '300px',
				})
				.afterClosed()
				.subscribe((option) => {
					if (option) {
						this.dialogRef.close(
							this.setDateToSend(completedBy, completed, responsibleOrganization, responsibleUser),
						);
					}
				});
			return;
		}
		this.dialogRef.close(this.setDateToSend(completedBy, completed, responsibleOrganization, responsibleUser));
	}
}
